import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";

export default function Brochures() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [brochures, setBrochures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchBrochures();
  }, []);

  const fetchBrochures = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_REQUESTING_API}/api/brochures`
      );
      setBrochures(res.data.data || res.data);
    } catch (error) {
      console.error("Failed to fetch brochures", error);
    } finally {
      setLoading(false);
    }
  };

  // Download PDF from GridFS
  const downloadPDF = async (brochureId, fileName) => {
    setDownloading(brochureId);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_REQUESTING_API}/api/brochures/download/${brochureId}`,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Hero Section with Landing Image */}
      <div className="relative w-full h-[75vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <img
          src="/images/mosque5.jpg"
          alt="Brochures Landing"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/70"></div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6">
              Our Brochures
            </h1>

            <div className="w-28 h-1 bg-white mx-auto mb-6 rounded-full"></div>

            <p className="text-lg md:text-2xl text-gray-200 leading-relaxed">
              Explore our mission, programs, and community initiatives through
              professionally designed digital brochures.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Brochure Cards Section */}
      <div className="max-w-7xl mx-auto py-20 px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Download Resources
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Access our collection of informative brochures and materials
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="text-center text-xl text-gray-500">
            Loading brochures...
          </div>
        )}

        {/* Empty State */}
        {!loading && brochures.length === 0 && (
          <div className="text-center text-xl text-gray-500">
            No brochures available
          </div>
        )}

        {/* Brochure Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {brochures.map((brochure, index) => (
            <motion.div
              key={brochure._id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className="group relative"
            >
              {/* Card Container */}
              <div className="relative h-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 transform transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                {/* PDF Preview Image */}
                <div className="relative h-64 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                  {/* Show uploaded preview image if available, otherwise placeholder */}
                  {brochure.image ? (
                    <img
                      src={brochure.image}
                      alt={brochure.pdfName}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <svg className="w-24 h-24 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="text-gray-500 font-medium">PDF Document</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Overlay on hover */}
                  <div className={`absolute inset-0 bg-gray-900/80 backdrop-blur-sm transition-opacity duration-300 ${hoveredIndex === index ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <svg className="w-16 h-16 text-white mx-auto mb-3 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <p className="text-white font-semibold">Click to Download</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors">
                    {brochure.pdfName}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {brochure.pdfDescription}
                  </p>

                  {/* Meta Information - Optional, can be added later */}
                  {/* <div className="flex items-center gap-4 mb-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>PDF</span>
                    </div>
                  </div> */}

                  {/* Download Button */}
                  <button
                    onClick={() => downloadPDF(brochure._id, brochure.pdfName)}
                    disabled={downloading === brochure._id}
                    className="relative w-full inline-flex items-center justify-center gap-2 bg-gray-800 text-white px-6 py-3 rounded-xl font-semibold overflow-hidden group/btn transition-all duration-300 hover:bg-gray-900 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {downloading === brochure._id ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download PDF
                        </>
                      )}
                    </span>
                    {!downloading && (
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 transform -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
                    )}
                  </button>
                </div>

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-0 h-0 border-t-[60px] border-t-gray-800 border-l-[60px] border-l-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="absolute -top-14 -right-0 w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Background Decoration */}
      <div className="fixed bottom-0 left-0 w-full h-64 bg-gradient-to-t from-gray-100 to-transparent pointer-events-none"></div>
    </div>
  );
}






// import React, { useState } from "react";
// import { motion } from "framer-motion";

// export default function Brochures() {
//   const [hoveredIndex, setHoveredIndex] = useState(null);

//   const brochures = [
//     {
//       title: "Brochures (English)",
//       description: "our organization's mission and services",
//       file: "images/Brochures/apsubaibroucher.pdf",
//       image: "/images/brochure-preview-1.jpg", // Add preview images
//       pages: "12 pages",
//       size: "2.5 MB"
//     },
//     {
//       title: "Community Programs Brochure",
//       description: "Detailed information about our community initiatives",
//       file: "/pdfs/community-programs.pdf",
//       image: "/images/brochure-preview-2.jpg",
//       pages: "8 pages",
//       size: "1.8 MB"
//     },
//     {
//       title: "Organization Overview",
//       description: "Learn about our history, vision, and achievements",
//       file: "/pdfs/organization-overview.pdf",
//       image: "/images/brochure-preview-3.jpg",
//       pages: "16 pages",
//       size: "3.2 MB"
//     },
//   ];

//   return (
//     <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
//       {/* Hero Section */}
//      {/* Hero Section with Landing Image */}
// <div className="relative w-full h-[75vh] flex items-center justify-center overflow-hidden">
  
//   {/* Background Image */}
//   <img
//     src="/images/mosque5.jpg" // 🔥 change this to your image path
//     alt="Brochures Landing"
//     className="absolute inset-0 w-full h-full object-cover"
//   />

//   {/* Dark Overlay */}
//   <div className="absolute inset-0 bg-black/70"></div>

//   {/* Content */}
//   <div className="relative z-10 text-center px-4 max-w-4xl">
//     <motion.div
//       initial={{ opacity: 0, y: 40 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.9, ease: "easeOut" }}
//     >
//       <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6">
//         Our Brochures
//       </h1>

//       <div className="w-28 h-1 bg-white mx-auto mb-6 rounded-full"></div>

//       <p className="text-lg md:text-2xl text-gray-200 leading-relaxed">
//         Explore our mission, programs, and community initiatives through
//         professionally designed digital brochures.
//       </p>
//     </motion.div>
//   </div>

// </div>


//       {/* Brochure Cards Section */}
//       <div className="max-w-7xl mx-auto py-20 px-6">
//         <motion.div
//           initial={{ opacity: 0, y: 30 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.6 }}
//           viewport={{ once: true }}
//           className="text-center mb-16"
//         >
//           <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//             Download Resources
//           </h2>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Access our collection of informative brochures and materials
//           </p>
//         </motion.div>

//         <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
//           {brochures.map((brochure, index) => (
//             <motion.div
//               key={index}
//               initial={{ opacity: 0, y: 30 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5, delay: index * 0.1 }}
//               viewport={{ once: true }}
//               onMouseEnter={() => setHoveredIndex(index)}
//               onMouseLeave={() => setHoveredIndex(null)}
//               className="group relative"
//             >
//               {/* Card Container */}
//               <div className="relative h-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 transform transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
//                 {/* PDF Preview Image */}
//                 <div className="relative h-64 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
//                   {/* Placeholder for PDF preview - replace with actual preview image */}
//                   <div className="absolute inset-0 flex items-center justify-center">
//                     <div className="text-center">
//                       <svg className="w-24 h-24 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
//                       </svg>
//                       <div className="text-gray-500 font-medium">PDF Document</div>
//                     </div>
//                   </div>
                  
//                   {/* Overlay on hover */}
//                   <div className={`absolute inset-0 bg-gray-900/80 backdrop-blur-sm transition-opacity duration-300 ${hoveredIndex === index ? 'opacity-100' : 'opacity-0'}`}>
//                     <div className="absolute inset-0 flex items-center justify-center">
//                       <div className="text-center">
//                         <svg className="w-16 h-16 text-white mx-auto mb-3 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
//                         </svg>
//                         <p className="text-white font-semibold">Click to Download</p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Card Content */}
//                 <div className="p-6">
//                   {/* Title */}
//                   <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors">
//                     {brochure.title}
//                   </h3>
                  
//                   {/* Description */}
//                   <p className="text-gray-600 text-sm mb-4 line-clamp-2">
//                     {brochure.description}
//                   </p>

//                   {/* Meta Information */}
//                   <div className="flex items-center gap-4 mb-6 text-sm text-gray-500">
//                     <div className="flex items-center gap-1">
//                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
//                       </svg>
//                       <span>{brochure.pages}</span>
//                     </div>
//                     <div className="flex items-center gap-1">
//                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
//                       </svg>
//                       <span>{brochure.size}</span>
//                     </div>
//                   </div>

//                   {/* Download Button */}
//                   <a
//                     href={brochure.file}
//                     download
//                     className="relative w-full inline-flex items-center justify-center gap-2 bg-gray-800 text-white px-6 py-3 rounded-xl font-semibold overflow-hidden group/btn transition-all duration-300 hover:bg-gray-900 hover:shadow-lg"
//                   >
//                     <span className="relative z-10 flex items-center gap-2">
//                       <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
//                       </svg>
//                       Download PDF
//                     </span>
//                     <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 transform -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
//                   </a>
//                 </div>

//                 {/* Corner Accent */}
//                 <div className="absolute top-0 right-0 w-0 h-0 border-t-[60px] border-t-gray-800 border-l-[60px] border-l-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
//                   <svg className="absolute -top-14 -right-0 w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
//                     <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
//                   </svg>
//                 </div>
//               </div>
//             </motion.div>
//           ))}
//         </div>

      
//       </div>

//       {/* Background Decoration */}
//       <div className="fixed bottom-0 left-0 w-full h-64 bg-gradient-to-t from-gray-100 to-transparent pointer-events-none"></div>
//     </div>
//   );
// }